import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  IconButton,
  styled,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material'
import moment from 'moment'
import { useContext, useEffect, useState } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { OpenInNew } from '@mui/icons-material'
import palette from '../../../theme/palette'

import { CardBox } from '../../../theme/styleComponents'
import { flexCenter, flexJustifySpaceBetween } from '../../../theme/styleConstants'
import {
  FetchBillPaymentsQuery,
  PaymentType,
  PaymentMethod,
  PaymentAllocationType,
  useFetchBillPaymentsLazyQuery,
  useReversePaymentMutation,
  useReturnCheckPaymentMutation,
  AccountType,
  PaymentSource
} from '../../../../generated'
// import { RefundFormModal } from '../billTransactions/RefundFormModal'
import { ConfirmationModal } from '../../common/ConfirmationModal'
import { ReturnCheckModal } from './ReturnCheckModal'
import { AuthContext } from '../../../context'
// import { ActionType } from '../../../context/AppContextReducer'
import { statusColors } from '../../../utils/billing'
import {
  BAD_USER_INPUT,
  GRAPHQL_QUERY_POLICY,
  PAYMENTS_ROUTE,
  QUERY_PARAMS,
  SOMETHING_WENT_WRONG,
  TRANSACTION_STATUS_LABELS
} from '../../../constants'
// import InfoTooltip from '../../common/InfoTooltip'
import { enumToLabel, formatCurrency, getProperPaymentMethod, hasAdminAccess } from '../../../utils'
import { Link } from 'react-router-dom'
import { printRefundCheck } from '../../../../services'
import { Alert } from '../../common/Alert'

type Payment = NonNullable<FetchBillPaymentsQuery['fetchBillPayments']['payments']>[number]
// type RefundModalState = {
//   payment: Payment
// } | null

const paymentAllocationStatusColors: Record<string, string> = {
  [PaymentAllocationType.Interest]: '#ED6C02',
  [PaymentAllocationType.Principal]: '#0288D1'
}

const Table = styled(MuiTable)(() => ({
  '& .MuiTableCell-root': {
    fontSize: '12px !important'
  }
}))

const BillTransactions = ({
  billId,
  caseId,
  triggerRefetch
}: {
  billId?: number
  caseId?: string
  triggerRefetch?: number
}) => {
  // const { isOpen, dispatch } = useContext(AppContext)
  const { currentUser } = useContext(AuthContext)
  const [payments, setPayments] = useState<FetchBillPaymentsQuery['fetchBillPayments']['payments']>([])
  const [printingRefundCheckId, setPrintingRefundCheckId] = useState<string | null>(null)
  // const [refundFormModalProps, setRefundFormModalProps] = useState<RefundModalState>(null)
  const [reversePaymentTarget, setReversePaymentTarget] = useState<Payment | null>(null)
  const [returnCheckTarget, setReturnCheckTarget] = useState<Payment | null>(null)

  const [fetchBillPayments, { refetch }] = useFetchBillPaymentsLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError(error) {
      console.log('error occured while fetching bill payments', error)
    },
    onCompleted(data) {
      const { fetchBillPayments } = data || {}
      const { response, payments } = fetchBillPayments || {}
      if (response?.status === 200) {
        setPayments(payments || [])
      }
    }
  })

  const [reversePaymentMutation, { loading: reversingPayment }] = useReversePaymentMutation({
    onError(error) {
      const { graphQLErrors } = error
      if (graphQLErrors?.length) {
        graphQLErrors.forEach(({ extensions }) => {
          if (extensions?.code === BAD_USER_INPUT) Alert.error(error.message)
          else Alert.error(SOMETHING_WENT_WRONG)
        })
      } else Alert.error(SOMETHING_WENT_WRONG)
    },
    onCompleted(data) {
      if (data?.reversePayment?.response?.status === 200) {
        Alert.success('Payment reversed successfully')
        setReversePaymentTarget(null)
        refetch?.()
      }
    }
  })

  const [returnCheckMutation, { loading: returningCheck }] = useReturnCheckPaymentMutation({
    onError(error) {
      const { graphQLErrors } = error
      if (graphQLErrors?.length) {
        graphQLErrors.forEach(({ extensions }) => {
          if (extensions?.code === BAD_USER_INPUT) Alert.error(error.message)
          else Alert.error(SOMETHING_WENT_WRONG)
        })
      } else Alert.error(SOMETHING_WENT_WRONG)
    },
    onCompleted(data) {
      if (data?.returnCheckPayment?.response?.status === 200) {
        Alert.success('Check marked as returned successfully')
        setReturnCheckTarget(null)
        refetch?.()
      }
    }
  })

  const visiblePayments =
    // Filter out refund and vendor payments
    payments?.filter(payment => payment.paymentSource !== PaymentSource.RefundPayment && !payment.vendor?.id) || []

  // The give-back leg of a returned check is a real, separately dated bank transaction — that is the
  // whole point of it, and it is what the bank reconciliation ticks. But as a top-level row in this
  // widget it reads as a second, negative payment that the firm never took, and it carries Delete /
  // Return Check buttons that must never be pressed on it. So it is lifted out of the list and shown
  // underneath the deposit it reverses.
  const reversalByOriginalId = new Map<string, Payment>()
  for (const payment of visiblePayments) {
    if (payment.returnedPaymentId) reversalByOriginalId.set(payment.returnedPaymentId, payment)
  }

  const allPayments = visiblePayments.filter(payment => !payment.returnedPaymentId)
  const hasAdminPermission = hasAdminAccess(currentUser)

  // const handleModalOpen = (payment: Payment) => {
  //   if (isEligibleForRefund(payment)) {
  //     setRefundFormModalProps({ payment })
  //     return dispatch({ type: ActionType.SET_IS_OPEN, isOpen: !isOpen })
  //   }
  // }

  // const isEligibleForRefund = (payment: Payment) => {
  //   if (!payment.transaction) return true
  //   const transaction = payment.transaction
  //   return (
  //     !!transaction.settledAt &&
  //     moment().diff(moment(transaction.settledAt), 'days') <= 180 &&
  //     transaction.status === TransactionStatus.SettledSuccessfully
  //   )
  // }

  const canReversePayment = (payment: Payment) => {
    if (payment.reconciledAt) {
      return false
    }

    // A returned check has already been reversed; deleting it again would be confusing.
    if (payment.returnedAt) {
      return false
    }

    if (payment.paymentSource === PaymentSource.RefundPayment) {
      return false
    }

    // Payment processed via Debit / Credit Card or ACH cannot be reversed
    if (payment.transaction?.transactionId) {
      return false
    }

    return hasAdminPermission
  }

  const getReversePaymentWarning = (payment: Payment) => {
    const lines: string[] = []
    lines.push(`Amount: ${formatCurrency(payment.amount)}`)
    lines.push(`Payment Method: ${getProperPaymentMethod(payment.paymentMethod)}`)
    lines.push(`Date: ${moment(new Date(payment.date).getTime()).format('MMM DD, YYYY')}`)
    lines.push('')
    lines.push('This action will:')
    lines.push('- Remove all payment and journal entry records for this transaction')

    if (payment.paymentAllocations?.length) {
      const billIds = [...new Set(payment.paymentAllocations.map(a => a.bill.id))]
      lines.push(`- Remove payment allocations and recalculate status for Bill(s): ${billIds.join(', ')}`)
    }

    if (payment.paymentSource === PaymentSource.ClientCreditPayment) {
      lines.push('- Decrease the client credit balance on this matter')
    }

    if (payment.account?.type === AccountType.Trust) {
      lines.push('- Decrease the client trust balance on this matter')
    }

    lines.push('- Reverse all affected account balances')
    lines.push('')
    lines.push('This action cannot be undone. Are you sure you want to proceed?')

    return lines.join('\n')
  }

  const handleReversePayment = () => {
    if (!reversePaymentTarget) return
    reversePaymentMutation({
      variables: { reversePaymentInput: { paymentId: reversePaymentTarget.id } }
    })
  }

  // A returned check follows the same eligibility rules as a delete, but is limited to check
  // payments that have not already been returned.
  const canReturnCheck = (payment: Payment) => {
    if (payment.paymentMethod !== PaymentMethod.Cheque) return false
    if (payment.returnedAt) return false
    return canReversePayment(payment)
  }

  const getReturnCheckSummary = (payment: Payment) => {
    const lines: string[] = []
    lines.push(`Amount: ${formatCurrency(payment.amount)}`)
    if (payment.referenceNumber) lines.push(`Check #: ${payment.referenceNumber}`)
    lines.push(`Date: ${moment(new Date(payment.date).getTime()).format('MMM DD, YYYY')}`)
    return lines.join('\n')
  }

  const handleReturnCheck = (returnNotes: string, returnDate: string) => {
    if (!returnCheckTarget) return
    returnCheckMutation({
      variables: {
        returnCheckInput: { paymentId: returnCheckTarget.id, returnNotes: returnNotes || undefined, returnDate }
      }
    })
  }

  const handlePrintRefundCheck = async (refundId: string) => {
    setPrintingRefundCheckId(refundId)
    try {
      await printRefundCheck(refundId)
    } catch (error: unknown) {
      console.error('Error printing refund check', error)
      Alert.error((error as Error)?.message || 'Failed to print check, please try again')
    } finally {
      setPrintingRefundCheckId(null)
    }
  }

  useEffect(() => {
    if (billId !== undefined || caseId !== undefined) {
      fetchBillPayments({ variables: { BillPaymentsInput: { billId, caseId } } })
    }
  }, [billId, caseId, fetchBillPayments, triggerRefetch])

  function PaymentAccordion({ payments, defaultExpanded }: { payments: Payment[]; defaultExpanded?: string }) {
    const [expandedTxnIdLocal, setExpandedTxnIdLocal] = useState<string | null>(defaultExpanded || null)
    return (
      <>
        {payments.map(payment => {
          const transaction = payment.transaction
          // const transactionSettled = transaction?.status === TransactionStatus.SettledSuccessfully || !transaction
          // const hasRefunds = !!payment.refunds?.length
          const hasPaymentAllocations = !!payment.paymentAllocations?.length
          const creator = `${payment.creator?.firstName || ''} ${payment.creator?.lastName || ''}`
          const showAccordion = hasPaymentAllocations
          // const showAccordion = (transactionSettled && hasRefunds) || hasPaymentAllocations
          // const sortedRefunds = [...(payment?.refunds || [])]
          // .map(refund => refund)
          // .sort((a, b) => Number(b?.createdAt) - Number(a?.createdAt))

          const transactionId = transaction?.transactionId || payment.referenceNumber || ''
          const isReturnedCheck = !!payment.returnedAt
          // Falls back to `amount` because the corrected flow leaves the deposit at its real value;
          // only checks returned under the old flow carry the amount in `returnAmount` alone.
          const displayAmount = isReturnedCheck ? payment.returnAmount ?? payment.amount : payment.amount
          const reversal = reversalByOriginalId.get(payment.id)

          const payee = {
            name: '',
            account: ''
          }

          if (transaction?.bankData) {
            payee.name = transaction.bankData.nameOnAccount
            payee.account = transaction.bankData.accountNumber
          } else if (transaction?.cardData) {
            payee.name = `${transaction.cardData.customerFirstName} ${transaction.cardData.customerLastName}`
            payee.account = transaction.cardData.cardNumber
          } else if (!transaction) {
            payee.name = `${payment.client?.firstName || ''} ${payment.client?.lastName || ''}`
            payee.account = payment.account?.name || ''
          }

          return (
            <Box key={payment.id} pr={3} pl={showAccordion ? 1 : 3} py={1} borderBottom="1px solid #E4E7EC">
              <Accordion elevation={0} square expanded={showAccordion && expandedTxnIdLocal === payment.id}>
                <AccordionSummary style={{ padding: 0 }}>
                  <Box display={'flex'} alignItems="flex-start" width="100%" flexWrap="wrap">
                    <Box flex="1 1 200px" display="flex" alignItems="center" minWidth={0}>
                      {showAccordion && (
                        <IconButton
                          size="small"
                          onClick={e => {
                            e.stopPropagation()
                            setExpandedTxnIdLocal(prev => (prev === payment.id ? null : payment.id))
                          }}
                          sx={{ mr: 1 }}
                        >
                          <ExpandMoreIcon
                            sx={{
                              transform: expandedTxnIdLocal === payment.id ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s ease-in-out'
                            }}
                          />
                        </IconButton>
                      )}

                      <Box minWidth={0}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          flexWrap="wrap"
                          columnGap={2}
                          rowGap={0.5}
                          width="100%"
                        >
                          <Typography
                            color="#0A2540"
                            fontWeight={500}
                            fontSize={14}
                            sx={{
                              overflowWrap: 'anywhere',
                              ...(isReturnedCheck ? { textDecoration: 'line-through', opacity: 0.7 } : {})
                            }}
                            dangerouslySetInnerHTML={{
                              __html: `ID: #${transactionId} - <span class="bill-timeline primary-blue">${
                                payment.paymentType === PaymentType.Debit ? '-' : ''
                              }${formatCurrency(displayAmount)}</span>`
                            }}
                          />
                          {transaction && (
                            <Chip
                              label={TRANSACTION_STATUS_LABELS[transaction?.status || 'FDS_PENDING_REVIEW']}
                              sx={{
                                color: statusColors[transaction?.status || 'FDS_PENDING_REVIEW'],
                                border: `1px solid ${statusColors[transaction?.status || 'FDS_PENDING_REVIEW']}50`,
                                backgroundColor: 'transparent',
                                fontSize: 12
                              }}
                            />
                          )}
                        </Box>

                        <Typography variant="subtitle1" color="secondary.light">
                          Payee: {payee.name}
                        </Typography>
                        <Typography variant="subtitle1" color="secondary.light">
                          Paid through: {getProperPaymentMethod(payment.paymentMethod)}
                        </Typography>
                        {payment.creator?.id && (
                          <Typography variant="subtitle1" color="secondary.light">
                            Recorded by: {creator}
                          </Typography>
                        )}
                        {!!payee.account && (
                          <Typography variant="subtitle1" color="secondary.light">
                            Account: {payee.account}
                          </Typography>
                        )}
                        {payment.paymentSource === PaymentSource.ClientCreditPayment && !hasPaymentAllocations && (
                          <Typography variant="subtitle1" color="secondary.light">
                            Type: Client Credits
                          </Typography>
                        )}
                        <Typography variant="subtitle1" color="secondary.light">
                          {moment(new Date(payment.date).getTime()).format('MMM DD, YYYY')}
                        </Typography>
                        {isReturnedCheck && (
                          <Chip
                            label="Returned Check"
                            size="small"
                            sx={{
                              color: 'error.main',
                              border: '1px solid #D32F2F50',
                              backgroundColor: 'transparent',
                              fontSize: 12,
                              my: 0.5
                            }}
                          />
                        )}
                        {isReturnedCheck && (
                          <Typography variant="subtitle1" color="secondary.light">
                            {payment.returnNotes ? `Return Reason: ${payment.returnNotes}` : 'Returned'}
                          </Typography>
                        )}
                        {reversal && (
                          <Box mt={0.5} pl={1.5} borderLeft="2px solid #D32F2F50">
                            <Typography variant="subtitle1" color="secondary.light">
                              Debited back on {moment(new Date(reversal.date).getTime()).format('MMM DD, YYYY')} -{' '}
                              {formatCurrency(reversal.amount)}
                              {reversal.account?.name ? ` from ${reversal.account.name}` : ''}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* Cannot refund client funds or client credits */}
                    {/* {payment.account?.type === AccountType.Operating &&
                      payment.paymentSource !== PaymentSource.ClientCreditPayment && (
                        <>
                          <Button
                            onClick={() => handleModalOpen(payment)}
                            disabled={!isEligibleForRefund(payment)}
                            variant="outlined"
                            color="primary"
                            size="small"
                          >
                            Refund
                          </Button>
                          {!isEligibleForRefund(payment) && (
                            <InfoTooltip color="secondary.light" info="Transaction is currently in process" />
                          )}
                        </>
                      )} */}

                    {payment.account?.type === AccountType.Trust && (
                      <Tooltip
                        title="Trust payments cannot be refunded from here. You can use the payables and deposit funds screens."
                        placement="top"
                      >
                        <IconButton size="small" color="primary" aria-label="Trust payment refund info">
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}

                    {canReversePayment(payment) && (
                      <Button
                        onClick={() => setReversePaymentTarget(payment)}
                        variant="outlined"
                        color="error"
                        size="small"
                        sx={{ ml: 1 }}
                      >
                        Delete
                      </Button>
                    )}

                    {payment.paymentMethod === PaymentMethod.Cheque && (canReturnCheck(payment) || isReturnedCheck) && (
                      <Button
                        onClick={() => setReturnCheckTarget(payment)}
                        variant="outlined"
                        color="warning"
                        size="small"
                        sx={{ ml: 1 }}
                        disabled={isReturnedCheck || !canReturnCheck(payment)}
                      >
                        {isReturnedCheck ? 'Returned' : 'Return Check'}
                      </Button>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ background: 'transparent' }}>
                  <Box>
                    {hasPaymentAllocations && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontSize: '13px' }}>
                          Payment Distribution
                        </Typography>

                        <Box sx={{ overflowX: 'auto' }}>
                          <Table size="small" aria-label="payment allocations">
                            <TableHead>
                              <TableRow>
                                <TableCell>Bill ID</TableCell>
                                <TableCell align="center">Type</TableCell>
                                <TableCell align="center">Amount</TableCell>
                                <TableCell align="center">Allocation Date</TableCell>
                                <TableCell align="center">Fee</TableCell>
                                <TableCell align="center">Cost</TableCell>
                                <TableCell align="center">Expense</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {payment.paymentAllocations?.map(allocation => {
                                const billId = allocation.bill.id
                                return (
                                  <TableRow key={allocation.id}>
                                    <TableCell>
                                      <Link
                                        to={`/billing/bill-detail/${billId}`}
                                        style={{
                                          color: palette.primary.main,
                                          textDecoration: 'none'
                                        }}
                                      >
                                        {billId}
                                      </Link>
                                    </TableCell>
                                    <TableCell align="center">
                                      <Chip
                                        size="small"
                                        label={enumToLabel(allocation.type)}
                                        sx={{
                                          color: paymentAllocationStatusColors[allocation.type],
                                          border: `1px solid ${paymentAllocationStatusColors[allocation.type]}50`,
                                          backgroundColor: 'transparent'
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell align="center">{formatCurrency(allocation.amount)}</TableCell>
                                    <TableCell align="center">
                                      {moment(new Date(allocation?.paymentDate).getTime()).format('MMM DD, YYYY')}
                                    </TableCell>
                                    <TableCell align="center">{formatCurrency(allocation?.fee || 0)}</TableCell>
                                    <TableCell align="center">{formatCurrency(allocation?.cost || 0)}</TableCell>
                                    <TableCell align="center">{formatCurrency(allocation?.expense || 0)}</TableCell>
                                  </TableRow>
                                )
                              })}
                            </TableBody>
                          </Table>
                        </Box>
                      </Box>
                    )}

                    {/* {hasRefunds && (
                      <Box mt={2.5}>
                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontSize: '13px' }}>
                          Refunds
                        </Typography>
                        <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small" aria-label="refunds">
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell align="center">Amount</TableCell>
                              <TableCell align="center">Status</TableCell>
                              <TableCell align="center">Paid through</TableCell>
                              <TableCell align="center">Check no</TableCell>
                              {hasAdminPermission && <TableCell align="center">Actions</TableCell>}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedRefunds?.map(refund => {
                              const canPrintRefundCheck =
                                hasAdminPermission &&
                                refund?.paymentMethod === PaymentMethod.Cheque &&
                                refund?.referenceNumber

                              return (
                                <TableRow key={refund?.id}>
                                  <TableCell>
                                    {moment(parseInt(refund?.createdAt || '')).format('MMM DD, YYYY')}
                                  </TableCell>
                                  <TableCell align="center">${refund?.amount}</TableCell>
                                  <TableCell align="center">
                                    {refund?.transaction?.status ? (
                                      <Chip
                                        size="small"
                                        label={TRANSACTION_STATUS_LABELS[refund.transaction.status]}
                                        sx={{
                                          color: statusColors[refund.transaction.status],
                                          border: `1px solid ${statusColors[refund.transaction.status]}50`,
                                          backgroundColor: 'transparent'
                                        }}
                                      />
                                    ) : (
                                      <Chip
                                        size="small"
                                        label={TRANSACTION_STATUS_LABELS[TransactionStatus.RefundSettledSuccessfully]}
                                        sx={{
                                          color: statusColors[TransactionStatus.RefundSettledSuccessfully],
                                          border: `1px solid ${
                                            statusColors[TransactionStatus.RefundSettledSuccessfully]
                                          }50`,
                                          backgroundColor: 'transparent'
                                        }}
                                      />
                                    )}
                                  </TableCell>
                                  <TableCell align="center">{getProperPaymentMethod(refund?.paymentMethod)}</TableCell>
                                  <TableCell align="center">
                                    {refund?.paymentMethod === PaymentMethod.Cheque ? refund?.referenceNumber : ''}
                                  </TableCell>
                                  {hasAdminPermission && (
                                    <TableCell align="center">
                                      {canPrintRefundCheck ? (
                                        <Button
                                          disabled={printingRefundCheckId === refund?.id}
                                          variant="outlined"
                                          color="primary"
                                          size="small"
                                          sx={{ width: '100px', height: '30px', p: 0, m: 0.5 }}
                                          endIcon={
                                            printingRefundCheckId === refund?.id && (
                                              <CircularProgress size={18} color="inherit" />
                                            )
                                          }
                                          onClick={() => handlePrintRefundCheck(refund?.id || '')}
                                        >
                                          Print Check
                                        </Button>
                                      ) : null}
                                    </TableCell>
                                  )}
                                </TableRow>
                              )
                            })}
                          </TableBody>
                        </Table>
                        </Box>
                      </Box>
                    )} */}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          )
        })}
      </>
    )
  }

  return (
    <CardBox mb={3}>
      <Box
        sx={{
          p: 3,
          borderBottom: '1px solid #E4E7EC',
          ...flexCenter,
          ...flexJustifySpaceBetween,
          flexWrap: 'wrap',
          gap: 1
        }}
      >
        <Typography variant="h6" fontWeight={400} color="gray900" fontSize="18px">
          Payments
        </Typography>
        {/* Matter Financials only — on Bill Detail this widget is scoped to one bill, not the matter. */}
        {caseId && billId === undefined && (
          <Button
            variant="text"
            size="small"
            endIcon={<OpenInNew fontSize="inherit" />}
            href={`${PAYMENTS_ROUTE}?${QUERY_PARAMS.MATTER}=${caseId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            View All Payments
          </Button>
        )}
      </Box>
      <Box sx={{ maxHeight: '500px', overflowY: 'auto' }}>
        <PaymentAccordion payments={allPayments} />
      </Box>
      {/* {refundFormModalProps?.payment?.amount && isOpen && (
        <RefundFormModal payment={refundFormModalProps.payment} refetch={refetch} />
      )} */}

      <ConfirmationModal
        isOpen={!!reversePaymentTarget}
        title="Delete Payment"
        message={reversePaymentTarget ? getReversePaymentWarning(reversePaymentTarget) : ''}
        confirmText="Delete Payment"
        cancelText="Cancel"
        variant="danger"
        isLoading={reversingPayment}
        onConfirm={handleReversePayment}
        onCancel={() => setReversePaymentTarget(null)}
      />

      <ReturnCheckModal
        isOpen={!!returnCheckTarget}
        summary={returnCheckTarget ? getReturnCheckSummary(returnCheckTarget) : ''}
        paymentDate={returnCheckTarget?.date}
        isLoading={returningCheck}
        onConfirm={handleReturnCheck}
        onCancel={() => setReturnCheckTarget(null)}
      />
    </CardBox>
  )
}

export default BillTransactions
